// Service
import { DropdownHttpService } from "@core/services/dropdown-http.service";
import { EventEmitterService } from "@core/services/event-emitter.service";
import { LoadingBarService } from "@ngx-loading-bar/core";
import { VipMemberVerificationDataService } from "./services/vip-member-verification-data.service";
// Angular & Core & NGRX
import {
  AfterViewInit,
  ChangeDetectorRef, Component, OnDestroy, OnInit
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { VipVerificationReason } from "@core/enums/vip-verification-reason.enum";
import { VipVerificationStatus } from "@core/enums/vip-verification-status.enum";
import { Dropdown } from "@core/models/dropdown.model";
import { Group } from "@core/models/group.model";
import { Pagination } from "@core/models/pagination.model";
import { VipMemberVerification } from "@core/models/vip-member-verification.model";
// RXJS
import { Observable, of, Subscription } from "rxjs";
import { map, tap } from "rxjs/operators";
// Third Party
import Swal from "sweetalert2";

@Component({
  templateUrl: "./vip-verification.component.html",
  styleUrls: ["./vip-verification.component.scss"],
})
export class VipVerificationComponent
  implements OnInit, OnDestroy, AfterViewInit {
  form: FormGroup;
  messages$ = this.vipMemberVerificationDataService.messages$;
  dropdown = {
    groups: this.dropdownHttpService.groups,
    statuses: this.dropdownHttpService.verificationStatuses,
    reason: this.dropdownHttpService.reason,
    perPage: this.dropdownHttpService.perPage,
  };
  dropdownSettings = {};
  vipVerificationStatus = VipVerificationStatus;
  vipVerificationReason = VipVerificationReason;
  members$: Observable<VipMemberVerification[]>;

  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = "";

  sortingStorageName = 'sortingConfig';
  sortingStorageGroup = '1.4';
  sortingConfig = {
    'id': 'desc',
    'username': 'desc',
    'current_group': 'desc',
    'next_member_group': 'desc',
    'total_deposits': 'desc',
    'reason': 'desc', // 1:Upgrade / 2: Downgrade
    'remarks': 'desc',
    'status': 'desc'
  };
  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };
  loading = false;
  dataLength = 0;

  private subscription = new Subscription();
  private refreshSubcription = new Subscription();
  private memberGroup: Dropdown[];
  private isAdmin: any;

  constructor(
    private loadingBar: LoadingBarService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private dropdownHttpService: DropdownHttpService,
    private eventEmitterService: EventEmitterService,
    private vipMemberVerificationDataService: VipMemberVerificationDataService
  ) {}

  ngOnInit() {
    localStorage.setItem('sortingConfig', JSON.stringify({ [this.sortingStorageGroup]: this.sortingConfig }));

    this.reload();
    this.formInit();
    this.memberGroupsInit();
    this.pagination = this.vipMemberVerificationDataService.pagination;
    this.dropdownSettings = {
      singleSelection: true,
      text: "Please Select",
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: "dropdown",
      primaryKey: "username",
      labelKey: "username",
      lazyLoading: true,
      noDataLabel: "",
      showCheckbox: false
    };
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.refreshSubcription.unsubscribe();
  }

  onSortColumn(property: string) {

    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if(key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }

      }
    }

    // User selection
    this.sortingSelection.sort_by = property;

    if(this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }
    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : "";
    this.loadingBar.start();
    return (this.members$ = this.vipMemberVerificationDataService
      .getWithQuery(`?page=${page}&perPage=${pageSize}&${this.generateSortingParam()}${params}`)
      .pipe(
        tap((res) => {
          this.loading = false;
          this.dataLength = res.length;
          this.pagination = this.vipMemberVerificationDataService.pagination;
          this.loadingBar.complete();
        })
      ));
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  getMemberGroupName(id: number) {
    if (this.memberGroup) {
      return id > 0
        ? (this.memberGroup.find((row) => row.id === id) as Group).name
        : "-";
    }
  }

  onClear() {
    this.formInit();
    this.onSubmit();
    this.eventEmitterService.onClearMemberSearch();
  }

  onSubmit() {
    of(this.form.value)
      .pipe(
        map(this.filterFormFields),
        tap((data) => {
          this.params = Object.keys(data)
            .map((key) => key + "=" + data[key])
            .join("&");
          this.page = 0;
        })
        /* Commented this part to avoid when submit API is called twice, so use the value 'this.page' onChange event to fire the function onViewPageBy*/
        // exhaustMap((data) => {
        //   this.params = Object.keys(data)
        //     .map((key) => key + "=" + data[key])
        //     .join("&");
        //   this.loadingBar.start();
        //   return (this.members$ = this.vipMemberVerificationDataService
        //     .getWithQuery(`?${this.params}&perPage=${this.pageSize}`)
        //     .pipe(
        //       tap((res) => {
        //         this.page = 1;
        //         this.pagination = this.vipMemberVerificationDataService.pagination;
        //         this.loadingBar.complete();
        //       })
        //     ));
        // })
      )
      .subscribe();
  }

  onUpdate(action: string, item: any) {
    Swal.fire({
      title: "Are you sure?",
      text:
        "Do you want to " +
        action +
        " this member " +
        item.reason_name +
        " from " +
        item.current_group +
        " to " +
        item.next_member_group +
        " ?",
      icon: "info",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    }).then((resp) => {
      if (resp.value) {
        if (action === "approve") {
          this.vipMemberVerificationDataService
            .updateStatus(item.id, 1)
            .subscribe(
              (res) => this.vipMemberVerificationDataService.messages$
            );
        } else {
          this.vipMemberVerificationDataService
            .updateStatus(item.id, 2)
            .subscribe(
              (res) => this.vipMemberVerificationDataService.messages$
            );
        }
      }
    });
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  private memberGroupsInit() {
    this.dropdown.groups
      .pipe(tap((groups) => (this.memberGroup = groups)))
      .subscribe();
  }

  private formInit() {
    this.form = new FormGroup({
      member_account_id: new FormControl(null),
      member_group_id: new FormControl("all"),
      status: new FormControl("all"),
      reason: new FormControl("all"),
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach((key) =>
      formData[key] !== "" && formData[key] !== null
        ? (fields[key] = formData[key])
        : key
    );
    return fields;
  }

  private reload() {
    this.onViewPageBy(this.page).subscribe();
  }
}
